import {useCallback, useState} from 'react'
import type {UpdateProjectRequest} from '@app/api-resolver/router'
import {FormFields} from './FormFields.tsx'
import {FormValues} from './FormValues.ts'
import {canUnarchiveProject, getUnarchiveTime, type Project} from '@package/shared'
import {useProjects} from '../../../../providers/projects/useProjects.ts'
import {EditModeButtons} from '../../../calendar/components/EditModeButtons.tsx'
import {useForm} from '@mantine/form'
import {useUserSettings} from '../../../../providers/userSettings/useUserSettings.ts'
import {Alert} from '@mantine/core'
import {IconAlertTriangle} from '@tabler/icons-react'
import {formatLocalDateTime} from '../../../connections/components/lastRefresh/formatter/formatLocalDateTime.ts'
import {zod4Resolver} from 'mantine-form-zod-resolver'

type Props = {
  project: Project
  onSuccess: () => void
  disabled: boolean
}

export const UpdateForm = ({project, onSuccess, disabled: containerDisabled}: Props) => {
  const {userSettings} = useUserSettings()
  const {updateProject, archiveProject, unarchiveProject} = useProjects()
  const [updating, setUpdating] = useState<boolean>(false)
  const [archiving, setArchiving] = useState<boolean>(false)

  const form = useForm<FormValues>({
    initialValues: project satisfies FormValues,
    validate: zod4Resolver(FormValues),
  })

  // update
  const handleSubmit = useCallback(
    async (formValues: FormValues) => {
      setUpdating(true)
      await updateProject({
        ...project,
        ...formValues,
      } satisfies UpdateProjectRequest).finally(() => setUpdating(false))
      onSuccess()
    },
    [project, onSuccess, updateProject],
  )

  // archive
  const handleArchive = useCallback(async () => {
    setArchiving(true)
    await archiveProject(project.id).finally(() => setArchiving(false))
    onSuccess()
  }, [project.id, archiveProject, onSuccess])

  // unarchive
  const handleUnarchive = useCallback(async () => {
    setUpdating(true)
    await unarchiveProject(project.id).finally(() => setUpdating(false))
    onSuccess()
  }, [unarchiveProject, project.id, onSuccess])

  const disabled = containerDisabled

  const archived = !!project.archivedTime
  const unarchiveDisabled = !canUnarchiveProject(project)
  const unarchiveTime = getUnarchiveTime(project)

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
      <form onSubmit={form.onSubmit(handleSubmit)} style={{display: 'flex', flexDirection: 'column', gap: 10}}>
        <FormFields form={form} disabled={disabled || archived} />
        {!archived && (
          <EditModeButtons
            submitVariant={'update'}
            submitting={updating}
            destructiveVariant={'archive'}
            withDestructiveLabel
            onDestruct={handleArchive}
            destructing={archiving}
            disabled={disabled}
          />
        )}
      </form>
      {archived && (
        <>
          {unarchiveDisabled && unarchiveTime && (
            <Alert variant={'light'} color={'orange'} icon={<IconAlertTriangle stroke={1} />}>
              This project can be unarchived at {formatLocalDateTime(unarchiveTime, userSettings)}.
            </Alert>
          )}
          <EditModeButtons
            submitVariant={'unarchive'}
            onSubmit={handleUnarchive}
            submitting={updating}
            disabled={disabled || unarchiveDisabled}
          />
        </>
      )}
    </div>
  )
}
